import Step from 'components/global/Step'
import UnlimitedCardConfigurator from './UnlimitedCardConfigurator'
import { useResolutions } from 'store/resolutions'
import { useApp } from 'store/steps'
import { useIntl } from 'react-intl'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import {
    addBlankCard,
    removeLastCard,
    setCardsVisible,
} from 'store/unlimited-slice'
import AddItem from 'components/shared/AddItem'
import UnlimitedBenefitsInfo from 'routes/unlimited-card/UnlimitedBenefitsInfo'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import steps from 'index'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useAutoScroll } from 'utils/hooks'
import { setErrors } from 'store/order-slice'
import { useEffect } from 'react'
import { pushDataLayer } from 'helpers/analytics'
import { setIsErrorModalOpened } from 'store/general-slice'
import QuantityPickerSection from 'components/shared/QuantityPickerSection'
import Text from 'components/shared/Text'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { useTicketPromotions } from 'routes/Tickets/utils/useTicketPromotions'
import { getDarkModeByLanguage } from 'helpers/theme'

const CardConfiguration = () => {
    const { renderActivePromotions } = useTicketPromotions()
    const { isTablet } = useResolutions()
    const { formatMessage } = useIntl()
    const { addCardsToCart, isProcessing } = useUnlimited()
    const dispatch = useAppDispatch()
    const { stepIndex, navigateToStep, language } = useApp()
    const { isDarkThemeForUnlimited } = usePromotions()
    const { setErrorMessages } = useErrorMessages()
    const { calculateScroll } = useAutoScroll()

    const { cardBoxes, areCardsVisible, people } = useAppSelector(
        (state) => state.unlimited
    )

    // TODO: check for selected photo
    const isContinuePossible = cardBoxes.every(
        ({
            personDetail,
            cardDetail,
            firstName,
            lastName,
            dateOfBirth,
            email,
            phone,
            image,
        }) =>
            personDetail &&
            cardDetail &&
            firstName &&
            lastName &&
            dateOfBirth &&
            email &&
            phone &&
            image
    )

    useEffect(() => {
        // < analytics
        pushDataLayer({
            event: 'productPresented',
            ecommerce: {
                items: [
                    {
                        item_name: 'Unlimited',
                        item_brand: 'PoP',
                    },
                ],
            },
        })
        // analytics >
    }, [])

    // TODO: check for photo
    const handleAddCardsToCart = async () => {
        dispatch(setErrors([]))

        // < analytics
        pushDataLayer({
            event: 'funnelStep',
            event_category: 'unlimited',
            event_action: 'unlimited_params_selected',
            event_label: '',
        })
        // analytics >

        addCardsToCart().then((response) => {
            const { isSuccess, messages } = response

            if (isSuccess) {
                const nextStepId = steps[stepIndex + 1].id
                navigateToStep({ stepId: nextStepId })
            } else {
                if (messages) {
                    const error = { messages }

                    if (
                        messages.some((error) =>
                            error.property_path.includes('detail')
                        )
                    )
                        setErrorMessages(error)
                    else dispatch(setIsErrorModalOpened(true))
                }
            }
        })
    }

    return (
        <>
            <Step
                title={formatMessage({
                    id: 'global.steps.caption_unlimited_card_configuration',
                    defaultMessage: 'Konfiguracja karty',
                })}
                isButtonDisabled={!isContinuePossible}
                isButtonLoading={isProcessing}
                isButtonVisible={areCardsVisible}
                onButtonClick={handleAddCardsToCart}
            >
                <>
                    {isTablet && <UnlimitedBenefitsInfo />}
                    <QuantityPickerSection
                        sectionHeader={formatMessage(
                            {
                                id: 'annual.config.section_caption',
                                defaultMessage: 'Wybierz liczbę kart',
                                description:
                                    'Keep the html tags in the translation',
                            },
                            {
                                span: (chunks: string[]) => (
                                    <>
                                        <Text
                                            as="span"
                                            variant="heading2"
                                            hasNoWrap
                                        >
                                            {chunks}
                                        </Text>
                                    </>
                                ),
                            }
                        )}
                        counterHeader={formatMessage({
                            id: 'annual.config.card_number',
                            defaultMessage: 'Liczba kart',
                        })}
                        minValue={1}
                        maxValue={9}
                        value={people}
                        onAdd={() => {
                            dispatch(addBlankCard())
                            calculateScroll({
                                trigerringSection: 'card_configuration',
                                trigerringAction: 'add',
                                targetId: 'cardIndexNumber',
                            })
                        }}
                        onSubstract={() => dispatch(removeLastCard())}
                        onSubmit={() => {
                            dispatch(setCardsVisible())
                            calculateScroll({
                                trigerringSection: 'card_configuration',
                                trigerringAction: 'add',
                                targetId: 'cardIndexNumber',
                            })
                        }}
                        hasDarkMode={isDarkThemeForUnlimited}
                        additionalInfo={renderActivePromotions({
                            sectionType: 'unlimited',
                            key: 'initialInfo',
                        })}
                        testIdPrefix="unlimited"
                    />
                    {areCardsVisible &&
                        cardBoxes.map((card, index) => (
                            <UnlimitedCardConfigurator
                                index={index}
                                key={`${index} ${card.cartItemId}`}
                                theme={
                                    isDarkThemeForUnlimited
                                        ? getDarkModeByLanguage(language)
                                        : undefined
                                }
                            />
                        ))}
                </>
                {areCardsVisible && isContinuePossible && (
                    <AddItem
                        onClick={() => {
                            dispatch(addBlankCard())
                            calculateScroll({
                                trigerringSection: 'card_configuration',
                                trigerringAction: 'add',
                                targetId: 'cardIndexNumber',
                            })
                        }}
                        testId="unlimited-button-add-another"
                    >
                        {formatMessage({
                            id: 'annual.btn.add_card',
                            defaultMessage: 'Dodaj kolejną kartę',
                        })}
                    </AddItem>
                )}
            </Step>
        </>
    )
}

export default CardConfiguration
